import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import useUser from 'utils/useUser';
import TrashDeleteIcon from 'components/atoms/icons/trashIcon';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { Button, Flex } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import s from './styles.module.scss';

import type { CallComment } from 'infrastructure/classes/comments/call-comment';
import type { Comment } from 'infrastructure/classes/comments/comment';

interface IGeneralCommentsListMessageItemProps {
  data: Comment | CallComment;
  onDeleteComment?: () => Promise<void>;
}

const GeneralCommentsListMessageItem: React.FC<
  IGeneralCommentsListMessageItemProps
> = (props) => {
  const { data, onDeleteComment } = props;

  const { t } = useTranslation();
  const { isAdmin } = useUser();
  const { Alert, showAlert } = useAlert();
  const [expanded, setExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const raf = requestAnimationFrame(() => {
      element.classList.add(s.truncated);
      const needsTruncation = element.scrollHeight > element.clientHeight + 1;
      element.classList.remove(s.truncated);
      setShouldTruncate(needsTruncation);
    });

    return () => cancelAnimationFrame(raf);
  }, [data.message]);

  const handleDelete = async () => {
    const message = `${t('labels.commentDeleteConfirm')} ${t(
      'labels.commentDeleteConfirmInteractiveTime',
    )}`;
    const alertAnswer = await showAlert({
      title: t('labels.commentDeleteTitle'),
      messageTitle: message,
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });

    if (alertAnswer) {
      await onDeleteComment?.();
    }
  };

  return (
    <div className={s['comment-item']} data-cy="general-comment-item">
      {isAdmin && (
        <div className={s['top-right-actions']}>
          <button
            className={s['delete-button']}
            onClick={handleDelete}
            aria-label="Delete comment"
          >
            <TrashDeleteIcon />
          </button>
        </div>
      )}
      {Alert}
      <p
        ref={contentRef}
        data-cy="general-comment-text"
        className={classNames(s['comment-message'], {
          [s.truncated]: !expanded && shouldTruncate,
        })}
      >
        {data.message}
      </p>
      {shouldTruncate && (
        <Button
          type="link"
          onClick={() => setExpanded(!expanded)}
          className={s['expand-button']}
        >
          <Flex gap={8}>
            {expanded ? (
              <>
                {t('labels.showLess', 'Show less')} <UpOutlined />
              </>
            ) : (
              <>
                {t('labels.showMore', 'Show more')} <DownOutlined />
              </>
            )}
          </Flex>
        </Button>
      )}
      <div data-cy="general-comment-footer" className={s['comment-signed-by']}>
        <p>
          <span>{`${t('labels.electronicallySignedBy')}: ${
            data.createdFullName
          }`}</span>
          <span> • </span>
          <span>
            {DateFormatter({
              date: data.createdAt,
              format: DateFormats['MM/dd/yy, hh:mm a'],
            })}
          </span>
        </p>
      </div>
    </div>
  );
};

export default GeneralCommentsListMessageItem;
